import { Children, ReactNode, useState } from 'react';
import { RiErrorWarningFill } from '@remixicon/react';
import {
  Tabs as MuiTabs,
  Tab,
  SvgIcon,
  Card,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';

interface TabItem {
  label: string;
  icon?: 'error' | 'warning';
}

interface TabsProps {
  className?: string;
  children?: ReactNode;
  tabs: TabItem[];
  variant?: 'tabs' | 'buttons';
}

/**
 * Specific implementation of tabs so it can be
 * reused in the application
 */
const Tabs = ({ className, tabs, children, variant = 'tabs' }: TabsProps) => {
  const [selectedTab, setSelectedTab] = useState(0);
  return (
    <div className={className}>
      {variant === 'tabs' ? (
        <MuiTabs
          className="mb-6"
          onChange={(_event, value) => {
            setSelectedTab(value as number);
          }}
          value={selectedTab}
          variant="fullWidth"
        >
          {tabs.map(({ label, icon }, index) => (
            <Tab
              key={index}
              label={label}
              iconPosition="start"
              icon={
                icon ? (
                  <SvgIcon
                    color={icon}
                    component={RiErrorWarningFill}
                    className="size-[16px]"
                  />
                ) : undefined
              }
            />
          ))}
        </MuiTabs>
      ) : (
        <Card className="mb-4 p-2">
          <ToggleButtonGroup
            exclusive
            value={selectedTab}
            onChange={(_, newValue) => {
              setSelectedTab(newValue != null ? Number(newValue) : 0);
            }}
          >
            {tabs.map(({ label, icon }, index) => (
              <ToggleButton key={index} value={index}>
                <span className="flex gap-4 items-center justify-around">
                  {icon && (
                    <SvgIcon
                      color={selectedTab === index ? 'inherit' : icon}
                      component={RiErrorWarningFill}
                      className="size-[16px]"
                    />
                  )}
                  <span>{label}</span>
                </span>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Card>
      )}
      <div>
        {Children.map(children ?? null, (childNode, index) => (
          <div
            key={index}
            className={selectedTab !== index ? 'hidden' : undefined}
          >
            {childNode}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Tabs;
